<template>
  <BContainer
    class="px-0"
    fluid
  >
    <BRow
      class="py-2 mb-2"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="gridEvents.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Event Date"
          label-for="filterEventDate"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterEventDate"
            v-model="gridEvents.filter.created_at"
            debounce="1000"
            type="date"
            :min="formEventValidation.eventDate.min"
            :max="formEventValidation.eventDate.max"
            :disabled="gridEvents.busy"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <BRow
      v-if="gridEvents.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridEvents.totalRows) <= 0 && !gridEvents.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No events found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6 mb-3"
    >
      <BCol
        v-for="(event, index) in gridEvents.rows"
        :key="index"
        cols="12"
        lg="6"
      >
        <Event
          :details="{
            id: event.id,
            province_name: event?.province ? (event.province?.province_name || '') : '',
            municipality_name: event?.municipality ? (event.municipality.municipality_name || '') : '',
            barangay_name: event?.barangay ? (event.barangay.barangay_name || '') : '',
            customer_name: event?.customer ? `${event.customer?.first_name || ''} ${event.customer?.last_name || ''}` : '',
            event_date: event.event_date,
            remarks: event.remarks
          }"
          :attachment-path="event.attachment_path || ''"
          :attachments="event.attachments || []"
          :loading="gridEvents.busy"
          @more:attachments="() => viewEvent(event)"
          @show:attachment="attachmentIndex => openPictures(event, attachmentIndex)"
        >
          <template #footer>
            <div class="d-flex justify-content-between">
              <div>
                {{ dateTimeShortFormatter(event.created_at) }}
              </div>
              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="warning"
                  @click.prevent="viewEvent(event)"
                >
                  View
                </BButton>
              </div>
            </div>
          </template>
        </Event>
      </BCol>
    </BRow>

    <BRow>
      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-md-0 mb-2"
      >
        <BSelect
          v-model="gridEvents.perPage"
          :options="gridEvents.pageOptions"
          :disabled="gridEvents.busy"
          class="w-100 w-md-75px"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1"
      >
        <BPagination
          v-model="gridEvents.currentPage"
          :total-rows="gridEvents.totalRows"
          :per-page="gridEvents.perPage"
          :disabled="gridEvents.busy"
          class="mb-0"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>
    </BRow>

    <BModal
      ref="modalViewEvent"
      size="lg"
      title="Task"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="eventModalShown"
      @hidden="eventModalHidden"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-event-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-lines" />
              <b class="is-tab-active ml-1">
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formEvent"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivityName"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityName"
                        v-model="formEvent.activity_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fEventDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fEventDate"
                        v-model="formEvent.event_date"
                        type="date"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="customer_id"
                    name="customer"
                    rules="required"
                  >
                    <BFormGroup
                      label="Customer"
                      label-for="fCustomer"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fCustomer"
                        v-model="formEvent.customer_name"
                        type="text"
                        placeholder="no selected customer"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fProvince"
                        v-model="formEvent.province_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipaliy"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fMunicipality"
                        v-model="formEvent.municipality_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formEvent.remarks ? formEvent.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formEvent.remarks"
                        placeholder="enter remarks here"
                        rows="7"
                        max-rows="9999"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-paperclip" />
              <b class="is-tab-active ml-1">
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formEvent.attachment_path"
            :files="formEvent.attachments"
            :loading="state.busy"
            :show-uploader="!state.editing"
          />
        </BTab>
        <BTab>
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-marker-alt" />
              <b class="is-tab-active ml-1">
                Location
              </b>
            </div>
          </template>

          <GeoLocationMap
            ref="geoLocationMap"
            :enabled="false"
            :coords="formEvent.geolocation"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton

            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            {{ state.editing ? 'Close' : 'Discard' }}
          </BButton>
        </div>
      </template>
    </BModal>

    <Preview
      ref="modalPreview"
    />
  </BContainer>
</template>
<script>
import MOMENT from 'moment'

import Event from '@/views/asm/officers/task-events/components/Event.vue'
import Attachment from '@/views/asm/officers/task-events/components/Attachment.vue'
import Preview from '@/views/asm/officers/other-events/components/Preview.vue'
import GeoLocationMap from '@/components/custom/GeoLocationMap.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'
import MBarangays from '@/mixins/locations/barangays'
import MCrops from '@/mixins/maintenances/crops'
import MCustomers from '@/mixins/accounts/customers'

import { SASMFsoTaskEvent } from '@/services/asm'

// import Products from '@/views/asm/officer-task-events/components/Products.vue'

export default {
  name: 'ASMOfficerTaskEvents',

  components: {
    Event,
    // Products,
    Attachment,
    Preview,
    GeoLocationMap
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MxImageUrl,
    MProvinces,
    MMunicipalities,
    MBarangays,
    MCrops,
    MCustomers
  ],

  props: {
    task: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        assignee_id: '',
        activity_id: '',
        customer_per_event: 1,
        schedule: ''
      })
    }
  },

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formEventValidation: {
        eventDate: {
          min: MOMENT(this.task.schedule).startOf('month').format('YYYY-MM-DD'),
          max: MOMENT(this.task.schedule).endOf('month').format('YYYY-MM-DD')
        }
      },
      formEvent: {
        id: '',
        activity_id: this.task.activity_id,
        activity_name: this.task.activity_name || '',
        task_id: this.task.id,
        event_date: '',
        customer_id: '',
        customer_name: '',
        province_id: '',
        province_name: '',
        municipality_id: '',
        municipality_name: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        products: [],
        geolocation: {
          longitude: 0,
          latitude: 0
        },
        active: 1
      },
      gridEvents: {
        busy: false,
        filter: {
          search: '',
          task: this.task.id,
          assignee: this.task.assignee_id,
          activity: this.task.activity_id,
          created_at: ''
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  watch: {
    'gridEvents.filter': {
      handler () {
        this.gridProvider()
      },
      deep: true
    },

    'gridEvents.currentPage' () {
      this.gridProvider()
    },

    'gridEvents.perPage' () {
      this.gridProvider()
    }
  },

  mounted () {
    this.gridProvider()
  },

  methods: {

    async gridProvider () {
      this.gridEvents.busy = true

      const payload = {
        page: this.gridEvents.currentPage,
        per_page: this.gridEvents.perPage,
        sort: this.gridEvents.sortBy,
        sort_desc: this.gridEvents.sortDesc,
        filter_text: this.gridEvents.filter.search,
        task_id: this.gridEvents.filter.task,
        assignee_id: this.gridEvents.filter.assignee,
        event_date: this.gridEvents.filter.created_at
      }

      return SASMFsoTaskEvent.get(payload).then(
        ({ data }) => {
          this.gridEvents.totalRows = data.total_rows
          this.gridEvents.rows = data.items
          return data.items
        }
      ).catch(() => {
        this.gridEvents.totalRows = 0
        this.gridEvents.rows = []
        return []
      }).finally(() => {
        this.gridEvents.busy = false
      })
    },

    openPictures (activity, index) {
      this.$refs.modalPreview.show(activity.attachments, activity.attachment_path, index)
    },

    viewEvent (event) {
      this.state.editing = true

      this.formEvent = Object.assign(this.formEvent, {
        id: event.id,
        task_id: this.task.id,
        activity_id: this.task.activity_id,
        activity_name: this.task?.activity_name || '',
        event_date: event.event_date,
        customer_id: event.customer_id,
        customer_name: event?.customer ? `${event?.customer?.first_name} ${event?.customer?.last_name}` : '',
        province_id: event.province_id,
        province_name: event?.province?.province_name || '',
        municipality_id: event.municipality_id,
        municipality_name: event?.municipality?.municipality_name || '',
        remarks: event.remarks,
        attachment_path: event.attachment_path,
        attachments: event.attachments || [],
        products: event?.purchases || [],
        geolocation: event?.geolocation || {
          longitude: 0,
          latitude: 0
        },
        active: event.active
      })

      this.$refs.modalViewEvent.show()
    },

    eventModalHidden () {
      this.detachArrowScroller('.nav-event-tabs')
    },

    eventModalShown () {
      this.attachArrowScroller('.nav-event-tabs')

      this.$refs.geoLocationMap.initMap()
    }
  }
}
</script>
